import { c as _c } from "react/compiler-runtime";
import { postHostedOnboardingPrefillChallenge } from '@onefootprint/fetch-hosted';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoPasskey16 } from '@onefootprint/icons';
import type { PrefillPostRequest } from '@onefootprint/request-types';
import { LinkButton, LoadingSpinner } from '@onefootprint/ui';
import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getBiometricChallengeResponse } from '@/idv2/utils/get-biometric-challenge-response';
import { getLogger } from '@/idv2/utils/logger';
import { isWebAuthnUserDismissal } from '@/idv2/utils/webauthn';
type PasskeyChallengeProps = {
  prefillMutation: UseMutationResult<void, unknown, PrefillPostRequest>;
  authToken: string;
};
const {
  logError,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'prefill-passkey-challenge'
});
export const PasskeyChallenge = t0 => {
  const $ = _c(16);
  const {
    prefillMutation,
    authToken
  } = t0;
  const {
    t
  } = useTranslation("idv2");
  const showRequestErrorToast = useRequestErrorToast();
  let t1;
  if ($[0] !== showRequestErrorToast) {
    t1 = error => {
      if (isWebAuthnUserDismissal(error)) {
        logWarn("Passkey prompt not completed", error);
        return;
      }
      logError("Failed to run passkey challenge", error);
      showRequestErrorToast("Something went wrong with signing in with passkey");
    };
    $[0] = showRequestErrorToast;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== authToken || $[3] !== prefillMutation) {
    t2 = async () => {
      const {
        data: t3
      } = await postHostedOnboardingPrefillChallenge({
        headers: {
          "X-Fp-Authorization": authToken
        },
        body: {
          challengeKind: "biometric"
        }
      });
      const {
        challengeData: t4
      } = t3;
      const {
        biometricChallengeJson,
        challengeKind,
        challengeToken
      } = t4;
      if (challengeKind !== "biometric" || !biometricChallengeJson) {
        throw new Error("Received incorrect challenge after requesting login biometric challenge");
      }
      const challengeResponse = await getBiometricChallengeResponse(biometricChallengeJson);
      await prefillMutation.mutateAsync({
        challengeResponse,
        challengeToken
      });
    };
    $[2] = authToken;
    $[3] = prefillMutation;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      onError: t1,
      mutationFn: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const passkeyMutation = useMutation(t3);
  if (passkeyMutation.isPending) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <LoadingSpinner className="min-h-[20px]" />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t4;
  if ($[9] !== passkeyMutation) {
    t4 = () => passkeyMutation.mutate();
    $[9] = passkeyMutation;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t(_temp);
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className="flex justify-center"><LinkButton iconComponent={IcoPasskey16} iconPosition="left" onClick={t4}>{t5}</LinkButton></div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
function _temp($) {
  return $.collectData.prefill.signInWithPasskey;
}